import { Carousel } from '@mantine/carousel'
import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import Autoplay from 'embla-carousel-autoplay'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import missingImage from '@/assets/webp_converted/imageOnload.webp'
import { useErrorMessages } from '@/constants/content'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'
import { saveLocation } from '@/plugins/auth/RouteRedirectors/utils'
import { Entry, WHATS_NEW_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'

import {
  GalleryElementsImagesByEntryId,
  pullGalleryImage,
  wrapData,
} from '../Discover/DiscoverCarousel'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'

interface NewsCardProps {
  image: string | undefined
  title: string
  description: string
  button: {
    text: string
    link: string
  }
  promptToSignIn?: boolean
}

export const NewsCard: React.FC<NewsCardProps> = ({
  image,
  title,
  description,
  button,
  promptToSignIn = false,
}) => {
  const { connect } = useAuth()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const handleNavigation = useCallback(() => {
    if (promptToSignIn && connect) {
      saveLocation(button.link)
      connect()
    } else if (button.link.startsWith('/')) {
      // ! TEMPORARY: to open Claim modal on wallet page on redirect from the carousel
      if (button.link === '/wallet') {
        saveLocation(button.link)
        navigate('/wallet', {
          state: {
            from: 'username',
          },
        })
      } else {
        saveLocation(button.link)
        navigate(button.link)
      }
    } else {
      window.open(button.link, '_blank')
    }
  }, [promptToSignIn, button.link, navigate])

  return (
    <Stack
      style={{
        cursor: 'pointer',
        width: '100%',
        height: isMobile ? 160 : 333,
        borderRadius: 12,
        backgroundImage: `url(${image || missingImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        marginBottom: 20,
      }}
      onClick={handleNavigation}
    >
      <Stack
        style={{
          position: 'absolute',
          bottom: isMobile ? 20 : 10,
          padding: isMobile ? '10px 10px' : '35px 30px',
          gap: 0,
        }}
      >
        {/* These need to be always white independent from dark mode to allow controlled contrast by cms images */}
        <Title order={isMobile ? 3 : 2} style={{ color: '#fff' }}>
          {title}
        </Title>
        <Text style={{ fontSize: isMobile ? 14 : 16, textAlign: 'left', color: '#fff' }}>
          {description}
        </Text>
      </Stack>
    </Stack>
  )
}

const WhatsNew: React.FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const autoplay = useRef(Autoplay({ delay: 10000 }))
  const { status } = useAuth()

  const errorMessages = useErrorMessages()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const [whatsNew, setWhatsNew] = useSessionStorage<Entry[]>(`whatsNew-${language}`, [])
  const [galleryImageByEntry, setGalleryImageByEntry] = useState<GalleryElementsImagesByEntryId>({})

  useEffect(() => {
    if (!whatsNew.length && !hasFetched) {
      setIsLoading(true)
      client
        .getEntries({ content_type: WHATS_NEW_CONTENT_TYPE, locale: language })
        .then((response: any) => {
          const sortedContent = response.items.sort(
            (a: Entry, b: Entry) => a.fields.order - b.fields.order
          )
          setWhatsNew(sortedContent)
          if (response.items.length < 1) {
            setHasFetched(true)
          }
        })
        .catch((err: any) => {
          setError(err.message)
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [whatsNew, setWhatsNew, t, language, hasFetched])

  useEffect(() => {
    if (whatsNew) {
      setGalleryImageByEntry(oldGalleryImageByEntry =>
        pullGalleryImage(whatsNew, oldGalleryImageByEntry, setGalleryImageByEntry)
      )
    }
  }, [whatsNew])

  return (
    <Stack style={{ gap: '60px' }}>
      <Stack gap="5px">
        <Title order={isMobile ? 2 : 1}>
          {t(`components.whatsNew.mainTitle1`, 'Own, Expand, & Maximize the Value of Your IP')}
        </Title>
        <Text style={{ fontSize: isMobile ? 15 : 22 }}>
          {t(
            `components.whatsNew.mainTitle2`,
            'Unlock autonomy, collaborate with fans, and monetize your work on LAMINA1'
          )}
        </Text>
      </Stack>
      {isLoading && <Loading />}
      {error && <ErrorMessage message={errorMessages.cmsConnection} details={error} />}
      {!isLoading && !error && (
        <Stack
          style={{
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Carousel
            draggable
            withIndicators
            withControls
            dragFree={false}
            slideGap={0}
            previousControlIcon={<IconCaretLeftFilled />}
            nextControlIcon={<IconCaretRightFilled />}
            plugins={[autoplay.current]}
            onMouseEnter={autoplay.current.stop}
            onMouseLeave={autoplay.current.reset}
            style={{
              height: '100%',
            }}
          >
            {whatsNew &&
              whatsNew.map((elem, index) => {
                const card = wrapData(elem, galleryImageByEntry)
                return (
                  ((status !== 'connected' && card.signedOut) ||
                    (status === 'connected' && card.signedIn)) && (
                    <Carousel.Slide
                      key={`${elem}${String(index)}`}
                      style={{ height: isMobile ? 160 : 333 }}
                    >
                      <NewsCard
                        image={card.image}
                        title={card.title}
                        description={card.paragraph}
                        button={{
                          text: card.buttonText || t('components.whatsNew.buttonText', 'Explore'),
                          link: card.buttonLink,
                        }}
                        promptToSignIn={status === 'disconnected'}
                      />
                    </Carousel.Slide>
                  )
                )
              })}
          </Carousel>
        </Stack>
      )}
    </Stack>
  )
}

export default WhatsNew
